// Note: Types for capacitor-plugin-cdv-purchase are auto-imported in tsconfig.json
// "types": [ ..., 'capacitor-plugin-cdv-purchase' ]

// eslint-disable-next-line import/no-unassigned-import
import 'capacitor-plugin-cdv-purchase'
import mitt from 'mitt'
import { convertError } from '@/models/error'
import { isNotUndefined } from 'typed-assert'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { platform as platformService } from '@/services/platform'
import {
  GoogleBillingUnavailableError,
  PlanChangeFailedError,
  UserCancelledError,
  allPlans,
  annualPlan,
  monthlyPlan,
} from '@/services/cordova.purchase.types'
import type {
  AppStore,
  PurchaseEvents,
} from '@/services/cordova.purchase.types'

// Internal event emitter to watch for store initialization and
// purchase events.
//
// The native plugin provides the "updated" callback that we set once during the
// initialization and then it is continuously called on any subscription update,
// including the initial plugin initialization process (it loads products and
// prices from app store), subscription to monthly or annual plan triggered
// by the user.
//
// ```
//  this.store
//    .when('subscription')
//    .updated((product) => ... )
//
//  this.store
//    .when('product')
//    .updated((product) => ... )
//    .approved((product) => ... )
//    .owned((product) => ... )
// ```
//
// In terms of our application logic this is inconvenient and it is better
// to have a regular `async` / `await` interface:
//
// ```
// await appStore.init()
// ... we can use the appStore now, display prices in the UI
// ..
// await appStore.subscribeMonthly()
// ... check the results, see if the user subscribed or not
// ...
// ```
//
// To move from the continously invoked store handlers to await variant,
// we use the event emitter to resolve or reject the awaiting promise
// when we reach a success or error state in the chain of subscription and
// product updates.
export const appStoreEmitter = mitt<PurchaseEvents>()

/**
 * Global object for Apple Payments / Google Play Billing operations.
 *
 * Wraps the `window.store` object provided by capacitor-plugin-cdv-purchase.
 */
export const appStore: AppStore = {
  // Global Cordova In-App Purchase plugin instance
  // We define `store` when initializing the object, because the CdvPurchase
  // namespace is not initialized yet at this point.
  store: undefined,

  debug: false,

  status: '',
  error: '',

  monthly: undefined,
  annual: undefined,

  _initialized: false,
  _deviceReady: false,
  _platform: undefined,

  /**
   * Initialize the `appStore` object.
   */
  async init(platform: 'ios' | 'android', isFreeUser = false): Promise<void> {
    this._platform = platform

    if (!this._deviceReady) {
      throw new Error(
        'Can not initialize AppStore client, device is not ready.',
      )
    }

    if (this._initialized) {
      // We can arrive here on repeated Billing page visits.
      return
    }

    if (!window.CdvPurchase) {
      throw new Error('window.CdvPurchase is not initialized yet!')
    }

    if (!CdvPurchase) {
      throw new Error(
        'CdvPurchase is not initialized but window.CdvPurchase is!?',
      )
    }

    const cdvPurchasePlatform =
      platform === 'ios'
        ? CdvPurchase.Platform.APPLE_APPSTORE
        : CdvPurchase.Platform.GOOGLE_PLAY

    isNotUndefined(CdvPurchase.store)
    this.store = CdvPurchase.store

    this.store.ready(() => {
      // If store is not ready, store.order will not work!
      isNotUndefined(this.store)
      this._debugLog('STORE ready')

      // Cordova-plugin-purchase in some cases doesn't fire any event at all when the
      // checkout for a plan change is completed successfully on Android. This is
      // because the plugin only fires events if there was a change to any of the
      // products. But with plan changes (at least with proration mode DEFERRED that we
      // use), there is no change to any of the products on checkout completion (only at
      // the next renewal when the new plan comes into effect there is a change).
      //
      // So to know that the checkout process was completed, we replace the plugin's
      // `onPurchasesUpdated` function, which gets called when the checkout process in
      // Google's native UI was completed.
      // We replace it with our own version that in addition to the normal logic also
      // triggers the `purchasesUpdated` event any time the function is called (i.e. even
      // if no products changed).
      if (this._platform === 'android') {
        const googlePlayAdapter = this.store.getAdapter(cdvPurchasePlatform)
        if (googlePlayAdapter !== undefined) {
          const onPurchasesUpdatedOrig = (
            googlePlayAdapter as CdvPurchase.GooglePlay.Adapter
          ).bridge.options?.onPurchasesUpdated
          if (onPurchasesUpdatedOrig !== undefined) {
            ;(
              googlePlayAdapter as CdvPurchase.GooglePlay.Adapter
            ).bridge.options.onPurchasesUpdated = (purchases: any): void => {
              onPurchasesUpdatedOrig(purchases)
              appStoreEmitter.emit('purchasesUpdated')
            }
          } else {
            Sentry.captureMessage(
              'Expected internal cordova-plugin-purchase function ' +
                'googlePlayAdapter.bridge.options.onPurchasesUpdated to be defined but is undefined.',
            )
          }
        } else {
          Sentry.captureMessage(
            'Expected cordova-plugin-purchase Google Play adapter to be defined but is undefined.',
          )
        }
      }
    })

    // We should first register all our products or we cannot use them in the app.
    this.store.register(
      allPlans(platform).map((id: string) => ({
        id,
        type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        platform: cdvPurchasePlatform,
      })),
    )

    if (this.debug) {
      // Debugging
      this.store.verbosity = 4
    }

    // Setup the receipt validator service. We use a custom receipt validator
    // function and have custom handling on our backend for this.
    this.store.validator = makeReceiptValidator(platform)

    this.store.error((error: CdvPurchase.IError) => {
      // On Android we can receive an error indicating that Google billing is
      // unavailable. This happens for example if the user is not signed
      // in into any Google account on the device, or the device has an old version of
      // Google Play.
      // When initializing the store, we ignore this error so that the initialization
      // succeeds. This is ok, as we fall back to displaying hard-coded default
      // data (price in USD).
      // We only want to ignore this error on initialization though. It also gets
      // triggered later when the user clicks subscribe. In that case it is handled
      // further up, and we show an error message to the user.
      if (
        this._platform === 'android' &&
        error.message.includes('BILLING_UNAVAILABLE') &&
        !this._initialized
      ) {
        this._debugLog('Got BILLING_UNAVAILABLE error')
        appStoreEmitter.emit('storeInit')
        this._initialized = true
        return
      }

      // With Google billing, the order error callback does not get triggered
      // in some cases, but this store.error() callback does.
      // So we also emit the productOrderError event here.
      // (Not sure if this is still the case with version 13 of
      // cordova-plugin-purchase, but it may still be good to include this.)
      if (this._platform === 'android') {
        appStoreEmitter.emit('productOrderError', [error, undefined])
      }

      this._debugLog('Store error', error)
      this.error = `Error ${error.code}: ${error.message}`
    })

    // Initialize the Cordova purchase plugin.
    // https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/classes/CdvPurchase.Store.md#initialize
    this.store
      .initialize([cdvPurchasePlatform])
      .then((errors: CdvPurchase.IError[] | undefined) => {
        if (errors && errors.length > 0) {
          console.error('this.store.initialize() has errors:', errors)
          if (!this._initialized) {
            console.error('this._initialized is false')
            appStoreEmitter.emit('storeInitError', errors)
          }
        } else if (!this._initialized && isFreeUser) {
          // A free user has an application receipt and no subscription, so
          // the plugin never emits `verified`. It then waits 10 seconds
          // before `receiptsVerified`. Prices are already loaded here.
          // Paid users still wait for `receiptsVerified` so their
          // subscription receipt can be checked first.
          this.updateProducts()
          this._initialized = true
          appStoreEmitter.emit('storeInit')
        }
      })
    // From the docs: "Replay the users transactions.
    // This method exists to cover an Apple AppStore requirement."
    this.store.restorePurchases()

    // Set up event listeners.
    // https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/interfaces/CdvPurchase.When.md
    this.store
      .when()
      .receiptUpdated((localReceipt: CdvPurchase.Receipt) => {
        // Called on all receipt updates. These can happen when the app is
        // initialized or when we make an order.
        this._debugLog('Receipt updated', localReceipt)
        localReceipt.transactions.forEach(
          (transaction: CdvPurchase.Transaction) => {
            this._onReceiptUpdated(transaction)
          },
        )
      })
      .receiptsVerified(() => {
        // This is called when all receipts have been verified, e.g. at startup.
        this._debugLog('Receipts verified')
        if (!this._initialized) {
          appStoreEmitter.emit('storeInit')
          this._initialized = true
          // Update the products on initialization.
          this.updateProducts()
        }
      })
      .approved((transaction: CdvPurchase.Transaction) => {
        // Approved for purchase, start the verification.
        this._debugLog(`Transaction approved ${JSON.stringify(transaction)}`)
        // We have to check whether the ID matches the subscription products,
        // because on iOS, a transaction with an ID equal to the bundle name is sent
        // every time the app starts:
        // https://github.com/j3k0/cordova-plugin-purchase/issues/1428#issuecomment-1727936885
        transaction.products?.forEach(
          (productInfo: { id: string; offerId?: string }) => {
            if (
              productInfo.id === monthlyPlan(platform) ||
              productInfo.id === annualPlan(platform)
            ) {
              transaction.verify()
            }
          },
        )
      })
      .verified((verifiedReceipt: CdvPurchase.VerifiedReceipt) => {
        // Verified with backend, finish the purchase.
        verifiedReceipt.finish()
        verifiedReceipt.sourceReceipt.transactions.forEach(
          (transaction: CdvPurchase.Transaction) => {
            transaction.products?.forEach(
              (productInfo: { id: string; offerId?: string }) => {
                isNotUndefined(this.store)
                if (this.store.owned(productInfo.id)) {
                  this._debugLog(`Product now owned: ${productInfo.id}`)
                }
              },
            )
          },
        )
      })
      .finished((transaction: CdvPurchase.Transaction) => {
        this._debugLog(`Transaction finished ${transaction.products}`)
      })

    return new Promise((resolve, reject) => {
      appStoreEmitter.on('storeInit', resolve)
      appStoreEmitter.on('storeInitError', reject)
    })
  },

  /**
   * Called from the `onDeviceReady` handler in init/native.app.ts
   */
  deviceReady(): void {
    this._deviceReady = true
  },

  /**
   * Fetch subscriptions data from AppStore.
   */
  updateProducts(): void {
    isNotUndefined(this._platform)
    isNotUndefined(this.store)
    this.monthly = this.store.get(monthlyPlan(this._platform)) || undefined
    this.annual = this.store.get(annualPlan(this._platform)) || undefined
  },

  /**
   * Show the monthly subscription dialog.
   *
   * The process should end either with `productOwned` or `productError`
   * event emitted through `appStoreEmitter`.
   *
   * @param isPlanChange Is this a change from the annual plan to the monthly plan.
   *  Should be set to true only when the plan is changed during an active subscription,
   *  not when resubscribing to another plan after the subscription had already expired.
   */
  async subscribeMonthly(isPlanChange: boolean): Promise<CdvPurchase.Product> {
    isNotUndefined(this._platform)
    isNotUndefined(this.store)
    const plan = monthlyPlan(this._platform)
    const otherPlan = annualPlan(this._platform)

    return order(this.store, this._platform, plan, isPlanChange, otherPlan)
  },

  /**
   * Show the annual subscription dialog.
   *
   * The process should end either with `productOwned` or `productError`
   * event emitted through `appStoreEmitter`.
   *
   * @param isPlanChange Is this a change from the monthly plan to the annual plan.
   *  Should be set to true only when the plan is changed during an active subscription,
   *  not when resubscribing to another plan after the subscription had already expired.
   */
  async subscribeAnnual(isPlanChange: boolean): Promise<CdvPurchase.Product> {
    isNotUndefined(this._platform)
    isNotUndefined(this.store)
    const plan = annualPlan(this._platform)
    const otherPlan = monthlyPlan(this._platform)

    return order(this.store, this._platform, plan, isPlanChange, otherPlan)
  },

  /**
   * Updates the product info, app store status, and emits subscriptionUpdatedOwned.
   */
  _onReceiptUpdated(transaction: CdvPurchase.Transaction) {
    this.updateProducts()

    if (transaction.products.length === 0) {
      return
    }

    const productId = transaction.products[0].id

    isNotUndefined(this._platform)
    isNotUndefined(this.store)

    this.status = 'Free'
    if (
      (productId === monthlyPlan(this._platform) ||
        productId === annualPlan(this._platform)) &&
      transaction.state === CdvPurchase.TransactionState.FINISHED
    ) {
      this.status = 'Subscribed'
      appStoreEmitter.emit('subscriptionUpdatedOwned')
    } else if (transaction.state === CdvPurchase.TransactionState.APPROVED) {
      this.status = 'Subscribing'
    }

    this._debugLog(
      'appstore receipt updated ' +
        `${this.status} ${productId} (transaction state: ${transaction.state})`,
      transaction,
    )
  },

  /**
   * Logs debug messages to the console if the `debug` flag is set to true.
   *
   * Also adds the message as a Sentry breadcrumb, regardless of the `debug` flag.
   */
  _debugLog(message: string, data: any = undefined) {
    if (this.debug) {
      console.log(message, data)
    }

    Sentry.addBreadcrumb({
      category: 'cordova-purchase',
      message: message,
      data: data,
    })
  },
}

async function order(
  store: CdvPurchase.Store,
  platform: 'ios' | 'android',
  planId: string,
  isPlanChange: boolean,
  otherPlanId: string,
): Promise<CdvPurchase.Product> {
  if (isPlanChange && platform === 'android') {
    // Do not allow plan changes on old Android app versions.
    // We switched to Google Billing v7 in app version 8.
    // TODO: this can be removed later, when we are sure all users are upgraded.
    // See https://github.com/shortformhq/main/issues/14038#issuecomment-2412468101
    const deviceInfo = await platformService.deviceInfo()
    const majorVersion = parseInt(deviceInfo.appVersion.split('.')[0])
    if (majorVersion < 8) {
      throw new PlanChangeFailedError(
        'Please update the application before changing the plan',
      )
    }

    await waitForProductRefresh(store, otherPlanId)
  }

  const product = store.get(planId)
  if (!product) {
    throw new Error(`Product ${planId} does not exist`)
  }
  const offer = product.getOffer()
  if (!offer) {
    throw new Error(`Offer for product ${planId} does not exist`)
  }

  return new Promise((resolve, reject) => {
    appStoreEmitter.on('productOrderOwned', (product) => {
      appStore._debugLog('productOrderOwned - resolving order')
      resolve(product)
    })

    appStoreEmitter.on('productOrderCancelled', () => {
      appStore._debugLog('productOrderCancelled - rejecting order')
      reject(new UserCancelledError())
    })
    appStoreEmitter.on('productOrderError', ([error, product]) => {
      if (
        error.message.includes('BILLING_UNAVAILABLE') &&
        platform === 'android'
      ) {
        appStore._debugLog(
          'productOrderError - BILLING_UNAVAILABLE error - rejecting order',
          error,
        )
        reject(new GoogleBillingUnavailableError())
      } else {
        appStore._debugLog(
          'productOrderError - generic error - rejecting order',
          error,
        )
        reject([error, product])
      }
    })
    appStoreEmitter.on('verificationDenied', (err) => {
      appStore._debugLog('verificationDenied - rejecting order', err)
      reject(err)
    })

    isNotUndefined(platform)

    const additionalData = getAdditionalPurchaseData(
      store,
      platform,
      otherPlanId,
      isPlanChange,
    )

    appStore._debugLog(
      `Subscribing to ${planId} with additionalData: `,
      additionalData,
    )

    // Execute the order.
    // https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/classes/CdvPurchase.Offer.md#order
    offer
      .order(additionalData)
      .then((error: CdvPurchase.IError | undefined) => {
        if (error) {
          if (error.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
            // Purchase flow has been cancelled by user
            appStoreEmitter.emit('productOrderCancelled', product)
          } else {
            // Other type of error, check error.code and error.message
            appStore._debugLog(`Product error ${product.id}`, error)
            appStoreEmitter.emit('productOrderError', [error, product])
          }
        }
      })
  })
}

/**
 * Returns true if the product specified by `productId` is in any transaction
 * of `receipt`. Otherwise, returns false.
 */
function productIsInReceipt(
  receipt: CdvPurchase.Receipt,
  productId: string,
): boolean {
  for (const transaction of receipt.transactions) {
    if (transaction.products) {
      for (const product of transaction.products) {
        if (product.id === productId) {
          return true
        }
      }
    }
  }
  return false
}

/**
 * Get additional data for the offer.order() call for Android plan changes.
 *
 * Returns undefined if the platform is not 'android', or if this is not a plan change.
 *
 * For Android plan changes, it is expected that there is a currently active subscription
 * with the same Google account that is now being used to do the plan change.
 * Throws an error with a message that can be displayed to users, if no purchase token
 * for a current subscription can be found.
 * Returns an object with fields `oldPurchaseToken` and `prorationMode` otherwise.
 *
 * Documentation on AdditionalData interface (we only use the Google Play part):
 * https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/interfaces/CdvPurchase.AdditionalData.md
 * https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/interfaces/CdvPurchase.GooglePlay.AdditionalData.md
 *
 * ---
 *
 * We have to set `oldPurchaseToken` and `prorationMode` for plan changes. This can be
 * seen in Google's documentation at
 * https://developer.android.com/google/play/billing/subscriptions#trigger-subscription
 * The important part:
 *
 *  > when upgrading or downgrading, you need to provide details for the current
 *  > subscription, the future (upgraded or downgraded) subscription, and the
 *  > replacement mode to use, as shown in the following example:
 *
 *  ```
 *  BillingFlowParams billingFlowParams = BillingFlowParams.newBuilder()
 *    ...
 *    .setSubscriptionUpdateParams(
 *        SubscriptionUpdateParams.newBuilder()
 *            // purchaseToken can be found in Purchase#getPurchaseToken
 *            .setOldPurchaseToken("old_purchase_token")
 *            .setSubscriptionReplacementMode(ReplacementMode.CHARGE_FULL_PRICE)
 *            .build())
 *  ```
 *
 *  Here, they use "replacement mode", which replaces "proration mode" in v6 of
 *  the billing library.
 *
 *  Currently, we continue to use proration mode since it is what
 *  cordova-plugin-purchase supports. A future version of the plugin should
 *  support replacement modes, at which point we should switch:
 *  https://github.com/j3k0/cordova-plugin-purchase/issues/1462
 *
 * ---
 *
 * @throws PlanChangeFailedError - If no purchase token for the current subscription can be retrieved.
 */
function getAdditionalPurchaseData(
  store: CdvPurchase.Store,
  platform: 'ios' | 'android',
  currentPlanId: string,
  isPlanChange: boolean,
): CdvPurchase.AdditionalData | undefined {
  if (platform === 'android' && isPlanChange) {
    // There are different kinds of "receipts" used by this plugin.
    // VerifiedReceipt: https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/classes/CdvPurchase.VerifiedReceipt.md
    // Receipt (the type of sourceReceipt): https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/classes/CdvPurchase.Receipt.md
    const mostRecentReceipt = store.verifiedReceipts[0].sourceReceipt
    // Check if the most recent receipt corresponds to the current plan ID.
    if (!productIsInReceipt(mostRecentReceipt, currentPlanId)) {
      appStore._debugLog(
        `PlanChangeFailedError: product ${currentPlanId} is not in receipt: ${JSON.stringify(
          mostRecentReceipt,
        )}`,
      )
      throw new PlanChangeFailedError()
    }
    // Find the most recent purchase token.
    if (
      'purchaseToken' in mostRecentReceipt &&
      mostRecentReceipt.purchaseToken
    ) {
      const googlePlayReceipt =
        mostRecentReceipt as CdvPurchase.GooglePlay.Receipt
      return {
        googlePlay: {
          oldPurchaseToken: googlePlayReceipt.purchaseToken,
          prorationMode: CdvPurchase.GooglePlay.ProrationMode.DEFERRED,
        },
      }
    } else {
      appStore._debugLog(
        `PlanChangeFailedError: No purchaseToken in receipt: ${JSON.stringify(
          mostRecentReceipt,
        )}`,
      )
      throw new PlanChangeFailedError()
    }
  }
  return undefined
}

/**
 * Waits until the product for the given `planId` is owned. Should be used before
 * initiating a plan change on Android with the plan ID of the initial product to ensure
 * that cordova-plugin-purchase has finished loading/refreshing the initial product.
 *
 * This helps ensure:
 *  - The purchase token of the initial product is available (which is needed to
 *   initiate the plan change)
 *  - Any other updates/refreshes triggered by the plugin don't interfere with our
 *   detection that the checkout process has finished (as these other things could also
 *   lead to the `purchasesUpdated` event being fired).
 *
 * Waits for a maximum of 10 seconds. If the product is not owned by then, the promise
 * gets rejected with a `PlanChangeFailedError`.
 *
 * ---
 *
 * This sometimes fails after a plan change in this sequence of events:
 * - Plan change from A to B is requested by the user.
 * - At the renewal time for A, Google notifies our backend of the expiry of A
 *   and newly active B. The backend now knows that B is active.
 * - But the frontend Store is not notified that the user now has plan B.
 * - The user does not refresh the app, so the store is not refreshed.
 * - Then if we try to change plan B to another plan,
 *   - We call `waitForProductRefresh(store, planId_B)` (this function).
 *     - receiptUpdated is not triggered, so subscriptionUpdatedOwned is not
 *       emitted
 *     - Plan B is not in the store's verified purchases, so
 *       store.owned(planId_B) returns false
 *     - `success()` is not called, so throw PlanChangeFailedError after 10s
 *
 * Practically, this is not much of a problem:
 * - In real usage, the user will probably refresh the app at some point in
 *   between plan changes.
 * - Even if they don't, the PlanChangeFailedError tells them to restart the
 *   app and try again.
 * - Restarting the app will re-initialize the store, populating the purchases
 *   with the user's subscription to plan B.
 *
 * A plan change after another plan change does not seem that common, so
 * restarting the app once is unlikely to be much of a burden.
 * This is just an annoyance I (Ronald) noticed when testing plan changes.
 */
function waitForProductRefresh(
  store: CdvPurchase.Store,
  planId: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Reject the promise after 10 seconds
    const timeoutId = setTimeout(
      () => reject(new PlanChangeFailedError()),
      10000,
    )

    function success(): void {
      clearTimeout(timeoutId)
      resolve()
    }

    // Set up an event listener to finish the function once the product becomes owned.
    appStoreEmitter.on('subscriptionUpdatedOwned', success)

    // The product might already be owned. In this case we won't receive the
    // `subscriptionUpdatedOwned` event.
    if (store.owned(planId)) {
      appStore._debugLog(`Product is already owned: ${planId}`)
      success()
    } else {
      appStore._debugLog(`Product is not owned: ${planId}`)
    }
  })
}

/**
 * Make a receipt validator function based on the platform.
 *
 * The function returned takes the receipt data as `validatorRequestBody` and
 * calls `callback` with the validator's (our backend's) response data.
 *
 * Documentation on the function interface:
 * https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/interfaces/CdvPurchase.Validator.Function.md
 */
function makeReceiptValidator(platform: 'ios' | 'android') {
  return async function (
    validatorRequestBody: CdvPurchase.Validator.Request.Body,
    callback: CdvPurchase.Callback<CdvPurchase.Validator.Response.Payload>,
  ): Promise<void> {
    return receiptValidator(platform, validatorRequestBody, callback)
  }
}

/**
 * Custom AppStore receipt validator.
 *
 * The purchase plugin validator implementation that sends backend
 * requests and performs the receipt validation.
 *
 * Instead of just setting the validator to our backend URL, we use this
 * custom validator function. The reason is that we want to add user auth data
 * to the request, so we know which user profile to update on the backend after
 * validating the request.
 *
 * We also use this custom validator because of the application receipts:
 *
 * Besides subscription receipts, the plugin also automatically creates
 * an "application" product that represents the application itself
 * (relevant for paid apps), this is mentioned in the release notes.
 * https://github.com/j3k0/cordova-plugin-purchase/blob/master/RELEASE_NOTES.md#application-receipt
 *
 * We do not want to actually validate application receipts (as opposed to
 * product/subscription receipts) with our backend for two reasons:
 *
 * - This is not necessary: our app is free and we worked without
 * validations so far.
 * - The application receipt data is different from the subscription
 * receipt data. It has the same set of fields, but many of the fields
 * in the application receipt are “null”, so we would either have to keep
 * two separate schemas for application receipt and subscription receipt
 * or have a weaker common schema allowing nullable fields.
 *
 * Documentation on the Validator.Request.Body:
 * https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/interfaces/CdvPurchase.Validator.Request.Body.md
 */
async function receiptValidator(
  platform: 'ios' | 'android',
  validatorRequestBody: CdvPurchase.Validator.Request.Body,
  callback: CdvPurchase.Callback<CdvPurchase.Validator.Response.Payload>,
): Promise<void> {
  const emptyValidatorResponseData = {
    id: validatorRequestBody.id || '',
    latest_receipt: true,
    // The transaction in validatorRequestBody does not match the
    // NativeTransaction type expected, so we just use a placeholder test
    // transaction instead.
    // We need `as const` because otherwise "test" will have type "string",
    // which is too wide when the type definition specifies type: "test".
    transaction: { type: 'test' as const },
  }

  const transactionToBeValidated = validatorRequestBody.transaction
  // If there is no transaction for a non-application receipt, something is wrong.
  if (
    validatorRequestBody.type !== 'application' &&
    !transactionToBeValidated
  ) {
    callback({ ok: false, data: emptyValidatorResponseData })
    return
  }

  // If this is an application receipt, skip validation.
  if (
    validatorRequestBody.type === 'application' &&
    (!transactionToBeValidated ||
      ('id' in transactionToBeValidated &&
        transactionToBeValidated.id &&
        transactionToBeValidated.id.endsWith('.application')))
  ) {
    appStore._debugLog(
      `Validating app: ${JSON.stringify(validatorRequestBody)}`,
    )
    callback({ ok: true, data: emptyValidatorResponseData })
    return
  }

  // If this is a receipt that has already been verified, skip validation.
  //
  // (This only works on Google billing. For Apple billing, the validator
  // request body may have "com.shortform.application" instead of the product
  // ID in the `id` field. And the receipt info is encoded, so it is difficult
  // to access the product ID from there.)
  //
  // We should not have any reason to verify a receipt that has already been
  // verified, since webhooks from Google inform our backend when a
  // subscription is renewed or expired.
  //
  // In fact, doing so causes problems with plan changes on Android. The expiry
  // monitor from the plugin checks for expired purchases, so at the time the
  // plan changes, the expired old plan gets re-validated after the new plan
  // takes effect. This causes the user billing to say that the user's
  // subscription is no longer valid. In more detail:
  // https://github.com/shortformhq/main/issues/12139#issuecomment-1933492374
  //
  // So we may as well disable validation of verified receipts to prevent
  // extraneous (and possibly bug-causing) calls to our backend validator.
  //
  // Unfortunately we can't access this.store.expiryMonitor directly, since
  // it's a private member, but we can skip validation here.
  if (validatorRequestBody.id) {
    isNotUndefined(appStore.store)
    const cdvPurchasePlatform =
      platform === 'ios'
        ? CdvPurchase.Platform.APPLE_APPSTORE
        : CdvPurchase.Platform.GOOGLE_PLAY
    // Get the product associated with this validator request.
    const product = appStore.store.get(
      validatorRequestBody.id,
      cdvPurchasePlatform,
    )
    if (product) {
      // Find the most recent verified purchase corresponding to this product.
      const verifiedPurchase = appStore.store.findInVerifiedReceipts(product)
      if (
        verifiedPurchase &&
        transactionToBeValidated &&
        // We need to check for the existence of `id` to prevent this error:
        // > Property 'id' does not exist on type 'ApiValidatorBodyTransactionWindows'.
        'id' in transactionToBeValidated &&
        transactionToBeValidated.id &&
        // Renewal transactions have IDs consisting of the original order ID
        // with a suffix. For example, after the first renewal, the order ID is
        // "{originalOrderId}..2".
        verifiedPurchase.transactionId?.startsWith(transactionToBeValidated.id)
      ) {
        // If the transaction IDs match, this transaction has already been
        // validated, so we send a dummy response.
        appStore._debugLog(
          `Receipt already validated, skipping: ${JSON.stringify(
            validatorRequestBody,
          )}`,
        )
        callback({ ok: true, data: emptyValidatorResponseData })
        return
      }
    }
  }

  isNotUndefined(appStore.store)

  // Validate the subscription receipt.
  appStore._debugLog(
    `Validating product: ${JSON.stringify(validatorRequestBody)}`,
  )
  let response
  try {
    response = await backend.postReceipt(platform, validatorRequestBody)
  } catch (error: any) {
    appStore._debugLog('Error validating product:', validatorRequestBody)
    appStore._debugLog('Error:', error)
    if (error.response && error.response.data) {
      appStore._debugLog('Error data:', error.response.data)
    }

    const convertedError = convertError(error)
    if (convertedError.isValidationError()) {
      appStoreEmitter.emit('verificationDenied', convertedError)
      // We have to mark the receipt as processed by calling the callback with true.
      // Otherwise, the purchase plugin would keep retrying the receipt verification,
      // and it would fail again and again.
      callback({ ok: true, data: emptyValidatorResponseData })
    }
    callback({ ok: false, data: emptyValidatorResponseData })
    return
  }

  // Handle validation success or error
  // Both validation success and error are 200 OK with following structure:
  //
  // Success:
  // {
  //     ok : true,
  //     data : {
  //         // List of purchases in the receipt. This populates VerifiedPurchases.
  //         collection: [...]
  //         // ID of the product that has been validated.
  //         id: '...',
  //         // Tell the plugin that we've used the latest receipt.
  //         // This is not really used, but it seems to be required. It's probably
  //         // harmless to set it to True.
  //         latest_receipt: true,
  //         // Native transaction, more or less verbatim from the Apple/Google API.
  //         transaction : {...}
  //     }
  // }
  //
  // Error:
  // {
  //     ok : false,
  //     // optional
  //     message : "The subscription is expired."
  // }
  //
  // Documentation on the response data we should call `callback` with:
  // https://github.com/j3k0/cordova-plugin-purchase/blob/master/api/modules/CdvPurchase.Validator.Response.md
  const responsePayload = response.data
  if (
    responsePayload &&
    responsePayload.ok &&
    responsePayload.data.collection &&
    responsePayload.data.collection.length > 0
  ) {
    // Get the product ID from the collection of purchases in this receipt.
    const productId = responsePayload.data.collection[0].id
    const product = appStore.store.get(productId || '')
    if (!product) {
      // This is theoretically possible, though in practice it should not happen.
      // In this case we don't know what the product is, so something is wrong.
      appStore._debugLog('Did not find product with id =', productId)
      callback({ ok: false, data: responsePayload.data })
    } else {
      appStoreEmitter.emit('productOrderOwned', product)
      callback(responsePayload as CdvPurchase.Validator.Response.SuccessPayload)
    }
  } else {
    appStore._debugLog('Validation error:', responsePayload)
    const errorPayload: CdvPurchase.Validator.Response.ErrorPayload = {
      ok: false,
    }
    if (responsePayload.data?.code) {
      appStore.error = `Error validating purchase: ${responsePayload.data.code}`
      errorPayload.code = responsePayload.data.code
    }
    if (responsePayload.error?.message) {
      errorPayload.message = responsePayload.error.message
    }
    callback(errorPayload)
  }
}

/**
Example Product data from Google:

{
  "className": "Product",
  "title": "Shortform - Monthly",
  "description": "",
  "platform": "android-playstore",
  "type": "paid subscription",
  "id": "plan_monthly",
  "offers": [
    {
      "className": "Offer",
      "id": "plan_monthly@base-plan-monthly",
      "pricingPhases": [
        {
          "price": "$23.99",
          "priceMicros": 23990000,
          "currency": "USD",
          "billingPeriod": "P1M",
          "billingCycles": 0,
          "recurrenceMode": "INFINITE_RECURRING",
          "paymentMode": "PayAsYouGo"
        }
      ],
      "productId": "plan_monthly",
      "productType": "paid subscription",
      "platform": "android-playstore",
      "type": "subs",
      "tags": [],
      "token": "AUj/YhhwzU8bpqqCTgw6j0VBD/KOdfRBJ3kM772DkZ1J2epZ+pOGiUmL7oUFsdGeBZE0/B/4C7I1qa64KJN0/jVmUSWnx54t0YXyDZO6ddOCiSa+46ZMGviQgU4r1f036YBX"
    }
  ]
}

Example Receipt data from Google:

{
  "className": "Receipt",
  "transactions": [
    {
      "className": "Transaction",
      "transactionId": "GPA.1111-1111-1111-11111",
      "state": "finished",
      "products": [
        {
          "id": "plan_monthly"
        }
      ],
      "platform": "android-playstore",
      "nativePurchase": {
        "orderId": "GPA.1111-1111-1111-11111",
        "packageName": "com.shortform.app",
        "productId": "plan_monthly",
        "purchaseTime": 1708089942060,
        "purchaseState": 0,
        "purchaseToken": "peg...HsQ",
        "quantity": 1,
        "autoRenewing": true,
        "acknowledged": false,
        "productIds": [
          "plan_monthly"
        ],
        "getPurchaseState": 1,
        "developerPayload": "",
        "accountId": "",
        "profileId": "",
        "signature": "P3V...6g==",
        "receipt": "{\"orderId\":\"GPA.1111-1111-1111-11111\",\"packageName\":\"com.shortform.app\",\"productId\":\"plan_monthly\",\"purchaseTime\":1708089942060,\"purchaseState\":0,\"purchaseToken\":\"peg...HsQ\",\"quantity\":1,\"autoRenewing\":true,\"acknowledged\":false}"
      },
      "purchaseId": "peg...HsQ",
      "purchaseDate": "2024-02-16T13:25:42.060Z",
      "isPending": false,
      "isAcknowledged": false,
      "renewalIntent": "Renew"
    }
  ],
  "platform": "android-playstore",
  "purchaseToken": "peg...HsQ",
  "orderId": "GPA.1111-1111-1111-11111"
}
*/
